import React from 'react';
import { useTranslation } from '@shared/core';
import type { FormikProps } from 'formik';
import type { EmailInputFields, EmailType, EmailEditPanelOption } from '@shared/components/EmailsAndEcards/Emails.types';
import GeneralMessageEmailForm from '@shared/components/EmailsAndEcards/components/EmailEditor/components/EmailEditPanel/components/GeneralMessageEmailForm';
import EcardEmailForm from '@shared/components/EmailsAndEcards/components/EmailEditor/components/EmailEditPanel/components/EcardEmailForm';
import { isValidDate, isCustomECardType } from '@apps/ecard/Ecard.utils';
import { useEcardsTelemetry } from '@apps/ecard/Ecard.telemetry';
import { usePostOfficeTelemetry } from '@apps/admin/postOffice/PostOffice.telemetry';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EcardType } from '@graphql/generated-base';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useToast } from '@withjoy/joykit';
import ThankYouEmailForm from './components/ThankYouEmailForm';
import { useQueryParamHelper } from '@shared/core/queryString';
import { notEmailOrUrlRegex } from '@shared/components/EmailsAndEcards/Emails.utils';
import { useGetValidReplyToAddresses } from './hooks/getValidReplyToAddresses';
import { usePhotoUrlBridge } from '@apps/admin/message/components/PhotoSelector/usePhotoUrlBridge';

interface UseEmailEditPanelControllerArgs
  extends Readonly<{
    emailType: EmailType;
    formik: FormikProps<EmailInputFields>;
    toggleEmailEditPanel: () => void;
    isPasswordToggleShown?: boolean;
    eventDate?: string;
    ecardDraftType?: EcardType;
    eventHandle?: string;
    eventDisplayName?: string;
    pages?: ReadonlyArray<EventPageFragment>;
    welcomePhotoUrl?: string;
  }> {}

export const useEmailEditPanelController = (args: UseEmailEditPanelControllerArgs) => {
  const { toast } = useToast();
  const isMobile = useIsMobileScreen();
  const { t, t2 } = useTranslation('emailsAndEcards');
  const tEmailEditor = t2('emailEditor');
  const tEmailEditorHeadlineError = t('emailEditor', 'headlineError');
  const tGeneralMessageEmailOptions = tEmailEditor.generalMessageEmailOptions;
  const tEcardEmailOptions = tEmailEditor.ecardEmailOptions;
  const { emailType, formik, toggleEmailEditPanel, isPasswordToggleShown, eventDate, ecardDraftType, eventHandle, eventDisplayName } = args;
  const telemetry = useEcardsTelemetry();
  const { modifyThankYouNote } = usePostOfficeTelemetry();
  const { getValueString } = useQueryParamHelper();
  const thankYouPropmt = `Write a ${getValueString('tone') || 'friendly'} ${getValueString('context') || ''} thank you note to ${getValueString('name') || 'my friend'} from ${
    eventDisplayName || ''
  } for the ${getValueString('items') || 'gift'}.`;
  const headlineIsNotValid = !notEmailOrUrlRegex.test(formik.values.headline);
  const validReplyToAddresses = useGetValidReplyToAddresses(eventHandle ?? '');

  const generalMessageEmailOptions: Array<EmailEditPanelOption> = [
    {
      id: 'includeWebsiteDetails-toggle',
      name: 'website information toggle',
      hed: tGeneralMessageEmailOptions[0].hed,
      dek: tGeneralMessageEmailOptions[0].dek,
      isOn: !!formik.values.includeWebsiteDetails,
      formikName: formik.getFieldProps('includeWebsiteDetails').name,
      onCange: () => {
        watchToggleChanges('includeWebsiteDetails', !formik.values.includeWebsiteDetails);
        formik.setTouched({ ...formik.touched, ['includeWebsiteDetails']: true });
      },
    },
    {
      id: 'includeEventPassword-toggle',
      name: 'password toggle',
      hed: tGeneralMessageEmailOptions[1].hed,
      dek: tGeneralMessageEmailOptions[1].dek,
      isOn: !!formik.values.includeEventPassword,
      formikName: formik.getFieldProps('includeEventPassword').name,
      isDisabled: !!formik.values.includeAppInstructions || (!formik.values.includeAppInstructions && !formik.values.includeWebsiteDetails),
      onCange: () => {
        watchToggleChanges('includeEventPassword', !formik.values.includeEventPassword);
        formik.setTouched({ ...formik.touched, ['includeEventPassword']: true });
      },
    },
    {
      id: 'includeAppInstructions-toggle',
      name: 'app instruction toggle',
      hed: tGeneralMessageEmailOptions[2].hed,
      dek: tGeneralMessageEmailOptions[2].dek,
      isOn: !!formik.values.includeAppInstructions,
      formikName: formik.getFieldProps('includeAppInstructions').name,
      onCange: () => {
        watchToggleChanges('includeAppInstructions', !formik.values.includeAppInstructions);
        formik.setTouched({ ...formik.touched, ['includeAppInstructions']: true });
      },
    },
  ];

  const ecardEmailOptions: Array<EmailEditPanelOption> = [
    {
      id: 'includeEventDate-toggle',
      name: 'event date toggle',
      hed: tEcardEmailOptions[0].hed,
      dek: eventDate || tEmailEditor.dateHidden,
      isOn: !!formik.values.includeEventDate,
      formikName: formik.getFieldProps('includeEventDate').name,
      isDisabled: !eventDate,
      onCange: () => {
        formik.setFieldValue('includeEventDate', !formik.values.includeEventDate);
        formik.setTouched({ ...formik.touched, ['includeEventDate']: true });
      },
    },
    {
      id: 'includeAddToCalendarLink-toggle',
      name: 'add to calendar link toggle',
      hed: tEcardEmailOptions[1].hed,
      dek: tEcardEmailOptions[1].dek,
      isOn: !!formik.values.includeAddToCalendarLink,
      formikName: formik.getFieldProps('includeAddToCalendarLink').name,
      isDisabled: !isValidDate(eventDate),
      onCange: () => {
        formik.setFieldValue('includeAddToCalendarLink', !formik.values.includeAddToCalendarLink);
        formik.setTouched({ ...formik.touched, ['includeAddToCalendarLink']: true });
      },
      isHidden: ecardDraftType && isCustomECardType(ecardDraftType),
    },
  ];

  const handleIncludeGuestNamesToggle = () => {
    const includeGuestsNamesValue = formik.getFieldMeta('includeGuestNames').value;
    formik.setFieldValue('includeGuestNames', !includeGuestsNamesValue);
  };

  const { selectedPhotos: emailSelectedPhotos, handlePhotosChange: handleEmailPhotosChange } = usePhotoUrlBridge(formik.values.imageUrl, (url: string) => {
    formik.setFieldValue('imageUrl', url);
    formik.setFieldValue('containerId', undefined);
    formik.setFieldValue('assetId', undefined);
  });

  const onSaveClick = () => {
    if (formik.dirty) {
      if (formik.touched.message && emailType === 'thankyou') {
        modifyThankYouNote(thankYouPropmt, formik.initialValues?.message || '', formik.values?.message || '');
      }
      formik.submitForm();
    }
    toast(tEmailEditor.doneTooltipText, {
      icon: '✅',
    });
    telemetry.editEmailDoneClick();
    toggleEmailEditPanel();
  };

  const watchToggleChanges = (formikField: string, newValue: boolean) => {
    if (formikField === 'includeAppInstructions') {
      if (newValue) {
        formik.setFieldValue('includeEventPassword', true);
      }
      if (!newValue && !formik.values.includeWebsiteDetails) {
        formik.setFieldValue('includeEventPassword', false);
      }
    } else if (formikField === 'includeWebsiteDetails') {
      if (!newValue && !formik.values.includeAppInstructions) {
        formik.setFieldValue('includeEventPassword', false);
      }
    }

    formik.setFieldValue(formikField, newValue);
  };

  // post office v2 future functionality
  // const handleLinkSelect = (selectedLink: GeneralMessageLinkType) => {
  //   formik.setFieldValue('selectedLink', selectedLink);
  // };

  const getEmailSpecificComponent = () => {
    switch (emailType) {
      case 'generic':
        return (
          <GeneralMessageEmailForm
            formik={formik}
            tEmailEditor={tEmailEditor}
            generalMessageEmailOptions={generalMessageEmailOptions}
            isPasswordToggleShown={isPasswordToggleShown}
            handleIncludeGuestNamesToggle={handleIncludeGuestNamesToggle}
            onPhotosChange={handleEmailPhotosChange}
            selectedPhotos={emailSelectedPhotos}
            validReplyToAddresses={validReplyToAddresses}
            // post office v2 future functionality:
            // handleLinkSelect={handleLinkSelect}
            // initialSelectedLink={formik.values.selectedLink!}
          />
        );
      case 'ecard':
        return (
          <EcardEmailForm
            formik={formik}
            tEmailEditor={tEmailEditor}
            ecardEmailOptions={ecardEmailOptions}
            handleIncludeGuestNamesToggle={handleIncludeGuestNamesToggle}
            onPhotosChange={handleEmailPhotosChange}
            selectedPhotos={emailSelectedPhotos}
          />
        );

      case 'thankyou':
        return (
          <ThankYouEmailForm
            formik={formik}
            tEmailEditor={tEmailEditor}
            onPhotosChange={handleEmailPhotosChange}
            selectedPhotos={emailSelectedPhotos}
          />
        );
      default:
        return null;
    }
  };

  return {
    tEmailEditor,
    generalMessageEmailOptions,
    onSaveClick,
    handleIncludeGuestNamesToggle,
    getEmailSpecificComponent,
    isMobile,
    headlineIsNotValid,
    tEmailEditorHeadlineError,
  };
};
